import { useMemo } from "react";

interface UseOtpStylesProps {
  disabled?: boolean;
  error?: boolean;
  width?: number;
}

interface OtpStyles {
  containerStyle: {
    display: string;
    width: string;
    maxWidth: string;
  };
  focusStyle: React.CSSProperties;
  inputStyle: React.CSSProperties;
  separatorWidth: number;
}

const calculateInputSize = (width: number) => {
  if (width === 0) return { size: 32, fontSize: 14, separatorWidth: 16 };

  const numInputs = 6;
  const numGaps = numInputs - 1;

  const minInputSize = 32;
  const maxInputSize = 56;
  const minGapRem = 0.25;
  const maxGapRem = 1;
  const minGapPx = minGapRem * 16;
  const maxGapPx = maxGapRem * 16;

  const minTotalWidth = numInputs * minInputSize + numGaps * minGapPx;
  const maxTotalWidth = numInputs * maxInputSize + numGaps * maxGapPx;

  let inputSize: number;
  let separatorWidth: number;

  if (width <= minTotalWidth) {
    inputSize = minInputSize;
    separatorWidth = minGapPx;
  } else if (width >= maxTotalWidth) {
    inputSize = maxInputSize;
    separatorWidth = maxGapPx;
  } else {
    const availableSpace = width - minTotalWidth;
    const maxAdditionalSpace = maxTotalWidth - minTotalWidth;
    const scale = availableSpace / maxAdditionalSpace;

    inputSize = Math.floor(
      minInputSize + scale * (maxInputSize - minInputSize),
    );
    separatorWidth = minGapPx + scale * (maxGapPx - minGapPx);
  }

  const fontSize = Math.max(14, Math.min(18, Math.floor(inputSize * 0.32)));

  return { size: inputSize, fontSize, separatorWidth };
};

export const useOtpStyles = ({
  disabled = false,
  error = false,
  width = 0,
}: UseOtpStylesProps): OtpStyles => {
  const { size, fontSize, separatorWidth } = useMemo(
    () => calculateInputSize(width),
    [width],
  );

  const inputStyle = useMemo(() => {
    const baseStyle: React.CSSProperties = {
      width: `${size}px`,
      height: `${size}px`,
      border: "1px solid",
      borderRadius: "8px",
      fontSize: `${fontSize}px`,
      fontWeight: "500",
      textAlign: "center",
      outline: "none",
      transition: "all 0.15s ease-in-out",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "var(--color-white)",
      color: "var(--color-text-darkest)",
      boxSizing: "border-box",
    };

    if (disabled) {
      return {
        ...baseStyle,
        borderColor: "var(--color-border-tertiary)",
        backgroundColor: "var(--color-background-secondary)",
        cursor: "not-allowed",
        color: "var(--color-text-disabled)",
      };
    }

    if (error) {
      return {
        ...baseStyle,
        borderColor: "var(--color-feedback-error)",
      };
    }

    return {
      ...baseStyle,
      borderColor: "var(--color-border-primary)",
      boxShadow: "none",
    };
  }, [size, fontSize, disabled, error]);

  const focusStyle = useMemo(() => {
    if (error) {
      return {
        borderColor: "var(--color-feedback-error)",
        boxShadow: "0 0 0 2px rgba(220, 38, 38, 0.2)",
      };
    }

    return {
      borderColor: "var(--color-border-light)",
    };
  }, [error]);

  const containerStyle = useMemo(
    () => ({
      display: "flex",
      width: "100%",
      maxWidth: "32rem",
      justifyContent: "center",
    }),
    [],
  );

  return {
    containerStyle,
    focusStyle,
    inputStyle,
    separatorWidth,
  };
};
